import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RefreshCw, Mail, Paperclip, Reply, Send, Inbox as InboxIcon, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatWhen = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString();
};

export const MailboxInbox = ({ getAuthToken }) => {
  const [mailboxes, setMailboxes] = useState([]);
  const [activeMailbox, setActiveMailbox] = useState(null);
  const [threads, setThreads] = useState([]);
  const [openMessage, setOpenMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [sending, setSending] = useState(false);

  const authConfig = useCallback(() => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } }), [getAuthToken]);

  const loadMailboxes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/mailboxes`, authConfig());
      const active = (res.data || []).filter(m => m.is_active !== false);
      setMailboxes(active);
      setActiveMailbox(prev => prev || active[0]?.id || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load mailboxes');
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  const loadThreads = useCallback(async (mailboxId) => {
    if (!mailboxId) return;
    try {
      const res = await axios.get(`${API}/api/mailboxes/${mailboxId}/messages`, authConfig());
      setThreads(res.data?.threads || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load inbox');
    }
  }, [authConfig]);

  useEffect(() => { loadMailboxes(); }, [loadMailboxes]);
  useEffect(() => { loadThreads(activeMailbox); }, [activeMailbox, loadThreads]);

  const handleSync = async () => {
    if (!activeMailbox) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/api/mailboxes/${activeMailbox}/sync`, {}, authConfig());
      toast.success(`Synced ${res.data.synced} messages`);
      await loadThreads(activeMailbox);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleOpen = async (message) => {
    try {
      const res = await axios.get(`${API}/api/mailboxes/${activeMailbox}/messages/${message.uid}`, authConfig());
      setOpenMessage(res.data);
      setReplyTo(res.data.from_email || '');
      setReplyBody('');
      setThreads(prev => prev.map(thread => ({
        ...thread,
        messages: thread.messages.map(m => (m.uid === message.uid ? { ...m, seen: true } : m)),
        unread: thread.messages.some(m => m.uid === message.uid) ? Math.max(0, thread.unread - (message.seen ? 0 : 1)) : thread.unread
      })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not open message');
    }
  };

  const handleReply = async () => {
    if (!replyBody.trim() || !replyTo.trim()) {
      toast.error('Add a recipient and a message');
      return;
    }
    setSending(true);
    try {
      const subject = openMessage.subject?.toLowerCase().startsWith('re:')
        ? openMessage.subject
        : `Re: ${openMessage.subject}`;
      await axios.post(`${API}/api/mailboxes/${activeMailbox}/send`, {
        to: replyTo.split(',').map(a => a.trim()).filter(Boolean),
        subject,
        body: replyBody,
        is_html: false,
        in_reply_to: openMessage.message_id || '',
        references: openMessage.references || ''
      }, authConfig());
      toast.success(`Reply sent from ${openMessage.mailbox_email}`);
      setReplyBody('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  const current = mailboxes.find(m => m.id === activeMailbox);

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
        <RefreshCw className="h-4 w-4 animate-spin" /> Loading inbox...
      </div>
    );
  }

  if (mailboxes.length === 0) {
    return (
      <div className="p-8 text-center" data-testid="mailbox-inbox-empty">
        <InboxIcon className="h-8 w-8 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No mailbox connected yet.</p>
        <p className="text-xs text-slate-400 mt-1">Go to Mailboxes to connect a Titan or other professional mailbox.</p>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4" data-testid="mailbox-inbox">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-800">Inbox</h2>
          <Select value={activeMailbox || ''} onValueChange={(v) => { setActiveMailbox(v); setOpenMessage(null); }}>
            <SelectTrigger className="w-64" data-testid="inbox-mailbox-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {mailboxes.map(m => (
                <SelectItem key={m.id} value={m.id}>{`${m.display_name} — ${m.email_address}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="inbox-sync-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync now
        </Button>
      </div>

      {openMessage ? (
        <div className="space-y-4" data-testid="mailbox-message-view">
          <Button variant="ghost" size="sm" onClick={() => setOpenMessage(null)} data-testid="back-to-threads-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to inbox
          </Button>
          <div className="border border-slate-200 rounded-xl p-4">
            <h3 className="font-semibold text-slate-800">{openMessage.subject}</h3>
            <p className="text-xs text-slate-500 mt-1">
              From <span className="font-medium">{openMessage.from_name || openMessage.from_email}</span>
              {openMessage.from_email ? ` <${openMessage.from_email}>` : ''} · {formatWhen(openMessage.date)}
            </p>
            <p className="text-xs text-slate-400">To {openMessage.to}</p>

            {openMessage.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {openMessage.attachments.map(att => (
                  <a
                    key={att.index}
                    href={`${API}/api/mailboxes/${activeMailbox}/messages/${openMessage.uid}/attachments/${att.index}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-2 py-1 rounded-lg"
                    data-testid={`attachment-${att.index}`}
                  >
                    <Paperclip className="h-3 w-3" /> {att.filename}
                    <span className="text-slate-400">({Math.max(1, Math.round(att.size / 1024))} KB)</span>
                  </a>
                ))}
              </div>
            )}

            <div className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-700 overflow-x-auto">
              {openMessage.html_body ? (
                <div dangerouslySetInnerHTML={{ __html: openMessage.html_body }} />
              ) : (
                <pre className="whitespace-pre-wrap font-sans">{openMessage.text_body}</pre>
              )}
            </div>
          </div>

          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Reply className="h-4 w-4" /> Reply from {openMessage.mailbox_email}
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} className="mt-1" data-testid="reply-to-input" />
            </div>
            <Textarea
              rows={5}
              placeholder="Write your reply..."
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              data-testid="reply-body-input"
            />
            <Button onClick={handleReply} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-reply-btn">
              {sending ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Send className="h-4 w-4 mr-2" />Send Reply</>}
            </Button>
          </div>
        </div>
      ) : threads.length === 0 ? (
        <div className="text-center py-12" data-testid="inbox-no-messages">
          <Mail className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No messages synced yet for {current?.email_address}.</p>
          <p className="text-xs text-slate-400 mt-1">Click "Sync now" to pull the latest 100 messages from the last 30 days.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-testid="inbox-thread-list">
          {threads.map(thread => {
            const latest = thread.messages.reduce((a, b) => (b.uid > a.uid ? b : a), thread.messages[0]);
            return (
              <button
                key={thread.thread_key}
                onClick={() => handleOpen(latest)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start justify-between gap-3"
                data-testid={`inbox-thread-${thread.latest_uid}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm truncate ${thread.unread > 0 ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                      {latest.from_name || latest.from_email || 'Unknown sender'}
                    </span>
                    {thread.messages.length > 1 && (
                      <Badge className="bg-slate-100 text-slate-600 text-[10px] rounded-md">{thread.messages.length}</Badge>
                    )}
                    {thread.unread > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 text-[10px] rounded-md">New</Badge>
                    )}
                  </div>
                  <p className={`text-sm truncate ${thread.unread > 0 ? 'text-slate-800' : 'text-slate-600'}`}>{thread.subject}</p>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{formatWhen(thread.latest_date)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MailboxInbox;
